import { Component, computed, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { LayoutService } from '@/layout/service/layout.service';
import { Ripple } from 'primeng/ripple';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { FormsModule } from '@angular/forms';
import { AppSidebar } from '@/layout/components/app.sidebar';
import { AppBreadcrumb } from '@/layout/components/app.breadcrumb';
import { AppProjectSelector } from '@/layout/components/app.project-selector';
import { UserService } from '@/core/services/user.service';
import { AuthService } from '@/core/services/auth.service';
import { SidebarService } from '@/core/services/sidebar-state.service';
import { LoggingService } from '@/logging';
import { ProjectLocalStorage } from '@/types/project';
import { SuprsendService } from '@/core/services/suprsend.service';
import { ApprovalPendingService } from '@/modules/approvals/services/approval-pending.service';
import { NotificationsBellComponent } from '@/layout/components/notifications-bell/notifications-bell.component';
import { filter } from 'rxjs';

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, Ripple, ButtonModule, MenuModule, AppBreadcrumb, AppSidebar, AppProjectSelector, NotificationsBellComponent],
    template: `
        <div class="topbar-start">
            <!-- El logo se movió al sidebar (app.sidebar.ts): ese ancho lo usa
                 ahora topbar-navigation para el breadcrumb. -->

            <!-- Botón de menú móvil -->
            <button pButton pRipple #layoutmenubutton type="button" class="topbar-menubutton p-trigger" text rounded severity="secondary" (click)="onMenuButtonClick()">
                <i class="pi pi-bars"></i>
            </button>

            <!-- Navigation with integrated project selector and breadcrumb -->
            <div class="topbar-navigation">
                <!-- Project Context (oculto en módulo calidad) -->
                @if(selectedProject && !isCalidadRoute()) {
                <div class="project-context-wrapper" style="position: relative; z-index: 1;">
                    <app-project-selector [compact]="true"></app-project-selector>
                    <span class="breadcrumb-separator">›</span>
                </div>
                }
                
                <!-- Breadcrumb Trail -->
                <div app-breadcrumb></div>
            </div>
        </div>
        
        <!-- Menu lateral oculto -->
        <div class="layout-topbar-menu-section">
            <div app-sidebar></div>
        </div>
        <!-- Perfil de usuario -->
        <div class="topbar-end">
            <!-- Campana unificada: aprobaciones pendientes (deep-link a /approvals/review/:id)
                 + notificaciones SuprSend (vía SDK Feed). Reemplaza el link de aprobaciones y el
                 bell nativo de SuprSend. -->
            <app-notifications-bell></app-notifications-bell>
            <!-- Mount del Inbox web-component de SuprSend: oculto (sus notificaciones se muestran en
                 la campana unificada). Se mantiene en el DOM para no alterar initSuprSend. -->
            <div id="suprsend-inbox" style="display: none"></div>
            <ul class="topbar-menu" #topbarmenu>
                <li class="profile-item topbar-item">
                    <button type="button" pRipple class="user-menu-trigger flex items-center cursor-pointer bg-transparent border-0 rounded-lg px-2 py-1 hover:bg-sym-surface transition-colors" (click)="userMenu.toggle($event)">
                        <div class="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-white font-semibold text-lg">
                            {{getUserInitial()}}
                        </div>
                        <div class="flex flex-col ml-3 items-start">
                            <!-- displayName() y no msalAccountInfo.name directo: la cuenta puede no
                                 estar resuelta todavía y el acceso sin navegación opcional tiraba
                                 un TypeError en CADA ciclo de detección de cambios. -->
                            <span class="text-sm font-semibold text-sym-ink leading-tight">{{displayName()}}</span>
                            <span class="text-xs font-medium text-sym-gray-500 leading-tight">{{userService.profileTypeInfo?.officeLocation}}</span>
                        </div>
                        <i class="pi pi-angle-down text-sym-gray-500 ml-3 text-xs"></i>
                    </button>

                    <!-- Menú nativo de PrimeNG: hereda sombra/borde/tema del preset en vez del
                         panel a mano anterior (sin borde ni elevación, se confundía con el fondo). -->
                    <p-menu #userMenu [model]="menuItems" [popup]="true" appendTo="body" styleClass="user-menu-panel"></p-menu>
                </li>
            </ul>
        </div>
    `,
    host: {
        class: 'layout-topbar'
    }
})
export class AppTopbar implements OnInit {
    menuItems: MenuItem[] = [
        // Antes navegaba vía href="" + <base href="/">, que fuerza una recarga completa
        // a la raíz (ModuleHomeComponent) en vez de una navegación SPA — command con
        // el Router hace lo mismo sin el reload.
        { label: 'Módulos', icon: 'pi pi-th-large', command: () => this.router.navigate(['/']) },
        // Antes un botón de engranaje aparte en el topbar (color primario, superficie,
        // esquema de sidebar/fondo — app.configurator.ts); se movió aquí para dejar el
        // topbar-end con solo campana + perfil.
        { label: 'Personalizar tema', icon: 'pi pi-cog', command: () => this.onConfigButtonClick() },
        { separator: true },
        { label: 'Cerrar sesión', icon: 'pi pi-sign-out', command: () => this.logout() },
    ];

    @ViewChild('searchinput') searchInput!: ElementRef<HTMLElement>;
    @ViewChild('layoutmenubutton') layoutMenuButton!: ElementRef<HTMLElement>;
    @ViewChild('topbarmenu') topbarMenu!: ElementRef<HTMLElement>;
    @ViewChild(AppSidebar) appSidebar!: AppSidebar;
    selectedProject: ProjectLocalStorage | null = null;
    isCalidadRoute = signal(false);

    el = inject(ElementRef);
    authService = inject(AuthService);
    private sidebarService = inject(SidebarService);
    userService = inject(UserService);
    pendingService = inject(ApprovalPendingService);

    constructor(
        public layoutService: LayoutService,
        private loggingService: LoggingService,
        private suprsendService: SuprsendService,
        private router: Router,
    ) {
    }

    /**
     * Nombre a mostrar en el perfil.
     *
     * Cae al correo cuando la cuenta no trae `name` (algunas cuentas de Entra no lo mandan en el
     * id token): antes quedaba un hueco en blanco al lado del avatar sin ninguna pista de con qué
     * cuenta se había entrado.
     */
    displayName(): string {
        const account = this.userService.msalAccountInfo;
        return account?.name || account?.username || '';
    }

    getUserInitial(): string {
        const name = this.displayName();
        return name ? name.charAt(0).toUpperCase() : '?';
    }

    ngOnInit(): void {
        this.sidebarService.loadState();

        // El LoggingService ya maneja la obtención del profile
        // Solo necesitamos suscribirnos para obtener la información cuando esté disponible
        this.userService.getProfileTypeInfo().subscribe((res) => {
            // console.log('Profile Type Info: ', res);
        });

        this.userService.selectedProject.subscribe((project) => {
            this.selectedProject = project;
        });

        // Detectar si estamos en el módulo de calidad para ocultar el selector de proyectos
        this.isCalidadRoute.set(this.router.url.includes('/calidad'));
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e) => {
            this.isCalidadRoute.set((e as NavigationEnd).urlAfterRedirects.includes('/calidad'));
        });

        // La inicialización ahora ocurre dentro del SuprsendService automáticamente
        this.suprsendService.isInitialized$.subscribe(initialized => {
            if (initialized) {
                console.log('SuprSend listo en el Topbar');
                const feed = this.suprsendService.getFeedInstance();
                console.log('Feed Client obtenido:', feed);
            }
        });
    }

    searchBarActive = computed(() => this.layoutService.layoutState().searchBarActive);

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    activateSearch(el: HTMLElement | null = null) {
        this.layoutService.layoutState.update((val) => ({
            ...val,
            searchBarActive: true
        }));
        setTimeout(() => {
            this.searchInput.nativeElement?.focus();
        }, 250);
    }

    deactivateSearch() {
        this.layoutService.layoutState.update((val) => ({
            ...val,
            searchBarActive: false
        }));
    }

    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }

    onThemeToggleClick() {
        this.layoutService.toggleTheme();
    }

    onSidebarButtonClick() {
        this.layoutService.layoutState.update((val) => ({
            ...val,
            rightMenuVisible: true
        }));
    }

    onProfileMenuButtonClick() {
        this.layoutService.layoutState.update((val) => ({
            ...val,
            rightMenuActive: true
        }));
    }

    logout() {
        console.log('LOGOUT: ')
        this.suprsendService.logout();
        this.authService.logout();
    }

}